import type { ClinicTreatment } from './clinic-treatment';

export const ClinicFeatures = {
  LibraryPlusContent: 'library_plus',
  Messages: 'messages',
  Notes: 'notes',
  Video: 'video',
  DailyActions: 'daily_actions',
  PatientGrouping: 'patient_grouping',
  EmailNotifications: 'email_notifications',
  CoachIris: 'coach_iris_ui',
  CoachIrisLegacy: 'coach_iris_insights', //FIXME: remove it and just use the base one
  BalloonOrders: 'balloon_orders',

  // Mobile-app-only clinic features. These are NOT used by Insights UI/API —
  // the mobile app still reads them, so they must stay valid (and toggleable
  // in VCS Admin). Do not remove until the mobile app is refactored.
  LibraryContent: 'library',
  BalloonTracking: 'balloon_tracking',
  HealthKitData: 'health_kit_data',
  BalloonTrackingMobile: 'balloon_tracking_mobile',
  BalloonTrackingPreview: 'balloon_tracking_preview',
} as const;
export type ClinicFeature = (typeof ClinicFeatures)[keyof typeof ClinicFeatures];

export type ClinicSettings = {
  ClinicID: string;
  features?: string[];
  weightUnit?: string;
  treatmentsOffered?: number[];
  custom_splash_url?: string;
  // When true, admins of this clinic are exempt from signing the data-sharing
  // agreement. Opt-out: absent/false preserves the default (signature required).
  skipDataAgreement?: boolean;
};

export const isValidClinicFeature = (x: string): x is ClinicFeature =>
  Object.values(ClinicFeatures).includes(x as ClinicFeature);

export function isDataAgreementSkipped(settings?: Partial<ClinicSettings>): boolean {
  return settings?.skipDataAgreement === true;
}

export function clinicHasFeatureEnabled(
  settings: Partial<ClinicSettings>,
  feature: ClinicFeature
): boolean {
  return Boolean(settings?.features?.includes(feature));
}

export function isDailyActionsEnabled(settings?: ClinicSettings) {
  return Boolean(settings?.features?.includes(ClinicFeatures.DailyActions));
}

export function isMessagesEnabled(settings?: ClinicSettings) {
  return Boolean(settings?.features?.includes(ClinicFeatures.Messages));
}

export function isVideoEnabled(settings?: ClinicSettings) {
  return Boolean(settings?.features?.includes(ClinicFeatures.Video));
}

export function isLibraryPlusContentEnabled(settings?: Partial<ClinicSettings>) {
  return Boolean(settings?.features?.includes(ClinicFeatures.LibraryPlusContent));
}

export function isPatientGroupingEnabled(settings?: ClinicSettings) {
  return Boolean(settings?.features?.includes(ClinicFeatures.PatientGrouping));
}

export function isCoachIrisEnabled(settings?: ClinicSettings) {
  return Boolean(
    settings?.features?.includes(ClinicFeatures.CoachIris) ||
      settings?.features?.includes(ClinicFeatures.CoachIrisLegacy)
  );
}

export const DEFAULT_WEIGHT_UNIT = 'kg';

export function getDefaultClinicSettings(clinicId: string | undefined): ClinicSettings {
  return {
    weightUnit: DEFAULT_WEIGHT_UNIT,
    features: [ClinicFeatures.Notes],
    ClinicID: clinicId || '',
    treatmentsOffered: [1, 5],
  };
}

export function findMostUsedWeightUnit(clinicsSettings: ClinicSettings[]) {
  const unitsVotes = clinicsSettings.reduce(
    (votes, s) => {
      if (!s.weightUnit) {
        return votes;
      }

      const unit = s.weightUnit;

      const unitVotes = votes[unit] ?? 0;

      return {
        ...votes,
        [unit]: unitVotes + 1,
      };
    },
    {} as { [k: string]: number }
  );

  if (Object.keys(unitsVotes).length === 0) {
    return DEFAULT_WEIGHT_UNIT;
  }

  const [unit] = Object.entries(unitsVotes).reduce((mostVoted, unitVotes) => {
    if (unitVotes[1] > mostVoted[1]) {
      return unitVotes;
    }

    return mostVoted;
  });

  return unit;
}

export function filterOfferedTreatments(settings: ClinicSettings, treatments: ClinicTreatment[]) {
  return treatments.filter((t) => settings.treatmentsOffered?.includes(parseInt(t.treatmentId)));
}
